import styles from "./MusicTrackItem.module.scss";
import { ISpotifyTrack, IMusicSuggestion } from "../../models/musicSuggestionModels";
import { classNames } from "primereact/utils";

interface IMusicTrackItemProps {
  track: ISpotifyTrack;
  variant: "search" | "suggestion";
  suggestion?: IMusicSuggestion; // Only provided for suggestion variant
  onSelect?: () => void; // For search variant - when user clicks to add
  onDelete?: () => void; // For suggestion variant - if own suggestion
  onUpvote?: () => void; // For suggestion variant
  disableUpvote?: boolean; // Disable upvote for own suggestions
  isPlaying?: boolean;
  onPlayToggle?: () => void;
}

export const MusicTrackItem = ({
  track,
  variant,
  suggestion,
  onSelect,
  onDelete,
  onUpvote,
  disableUpvote = false,
  isPlaying = false,
  onPlayToggle,
}: IMusicTrackItemProps) => {
  const handleClick = () => {
    if (variant === "search" && onSelect) {
      onSelect();
    }
  };

  const canDelete = variant === "suggestion" && onDelete;
  const showUpvote = variant === "suggestion" && onUpvote;

  return (
    <div
      className={classNames(styles.trackItem, {
        [styles.clickable]: variant === "search",
        [styles.suggestion]: variant === "suggestion",
      })}
      onClick={handleClick}
    >
      {/* Album Art */}
      <div className={styles.albumArt}>
        {track.albumArt ? (
          <img src={track.albumArt} alt={track.name} />
        ) : (
          <div className={styles.noImage}>
            <i className="pi pi-music" />
          </div>
        )}
      </div>

      {/* Track Info */}
      <div className={styles.trackInfo}>
        <div className={styles.trackName}>{track.name}</div>
        <div className={styles.trackArtist}>{track.artist}</div>
      </div>

      {/* Actions */}
      <div className={styles.actions}>
        {/* Preview Button */}
        {track.previewUrl && onPlayToggle && (
          <button
            className={classNames(styles.actionButton, styles.playButton, {
              [styles.playing]: isPlaying,
            })}
            onClick={(e) => {
              e.stopPropagation();
              onPlayToggle();
            }}
            title={isPlaying ? "Pause preview" : "Play preview"}
          >
            <i className={isPlaying ? "pi pi-pause" : "pi pi-play"} />
          </button>
        )}

        {/* Suggestion-specific actions */}
        {variant === "suggestion" && (
          <>
            {/* Upvote Button - Always show for suggestions */}
            {showUpvote && (
              <button
                className={classNames(styles.actionButton, styles.upvoteButton, {
                  [styles.upvoted]: suggestion?.hasUserUpvoted,
                  [styles.disabled]: disableUpvote,
                })}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!disableUpvote) {
                    onUpvote?.();
                  }
                }}
                disabled={disableUpvote}
                title={
                  disableUpvote
                    ? "Cannot upvote your own suggestion"
                    : suggestion?.hasUserUpvoted
                    ? "Remove upvote"
                    : "Upvote"
                }
              >
                <i className="pi pi-thumbs-up" />
                <span>{suggestion?.upvoteCount || 0}</span>
              </button>
            )}

            {/* Delete Button */}
            {canDelete && (
              <button
                className={classNames(styles.actionButton, styles.deleteButton)}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete?.();
                }}
                title="Delete suggestion"
              >
                <i className="pi pi-trash" />
              </button>
            )}
          </>
        )}

        {/* Search variant - Add button */}
        {variant === "search" && (
          <button
            className={classNames(styles.actionButton, styles.addButton)}
            onClick={(e) => {
              e.stopPropagation();
              onSelect?.();
            }}
            title="Add to suggestions"
          >
            <i className="pi pi-plus" />
          </button>
        )}
      </div>
    </div>
  );
};
